import React, { useState } from 'react';
import { InfoPanel } from '@css-xp/base';
import Button from '@atlaskit/button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Box, Flex, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Popup from '@atlaskit/popup';
import './sign-up-banner.scss';

interface SignUpBannerProps {
  email: string;
  handleCreateAtlassianAccount: () => void;
  version?: number;
}

export const SignUpBanner = ({ email, handleCreateAtlassianAccount, version }: SignUpBannerProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const needAtlassianAccountTitle = React.useMemo(() => {
    if (version === 2) {
      return 'Email not registered as an Atlassian account';
    }

    return 'This email is not registered as an Atlassian account';
  }, [version]);

  const needAtlassianAccountMessage = (
    <>
      {isDetailsVisible && (
        <div>
          Select <span className="fw-6">Create Atlassian account</span> to register this email address. We’ll send an
          email to <span className="fw-6">{email}</span> so they can verify their email and complete the signup process.
        </div>
      )}
      <div className="action-buttons">
        <Button
          appearance="link"
          testId={`create-account-${email}`}
          onClick={() => {
            handleCreateAtlassianAccount();
          }}
          spacing="none"
        >
          Create Atlassian account
        </Button>
        {!version && (
          <>
            <div className="dot-text">.</div>
            <Button
              appearance="link"
              testId={`view-more-${email}`}
              onClick={() => {
                setIsDetailsVisible(!isDetailsVisible);
              }}
              spacing="none"
            >
              {isDetailsVisible ? 'Read less' : 'Read more'}
            </Button>
          </>
        )}
      </div>
    </>
  );

  if (version === 2) {
    return (
      <Box paddingBlockStart="space.200" testId={`signup-banner-v2-${email}`}>
        <Flex alignItems="center" gap="space.100">
          <WarningIcon label="Warning" size="medium" primaryColor={token('color.icon.warning')} />
          <Box>
            <Text testId={`warning-text-${email}`}>This email isn't registered with Atlassian.</Text>
            <Popup
              testId={`popup-content-${email}`}
              isOpen={isDetailsVisible}
              onClose={() => setIsDetailsVisible(false)}
              placement="bottom-start"
              content={() => (
                <div className="popup-content">
                  <Text>
                    To register this email address, please provide the user's full name. We'll send an email to {email}{' '}
                    so they can verify their email and complete the signup process.
                  </Text>
                </div>
              )}
              trigger={(triggerProps) => (
                <Button
                  {...triggerProps}
                  spacing="none"
                  appearance="link"
                  testId={`read-more-${email}`}
                  onClick={() => setIsDetailsVisible((prev) => !prev)}
                >
                  Read more
                </Button>
              )}
            />
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <div className="sign-up-banner" data-testid={`sign-up-banner-${email}`}>
      <InfoPanel type="warning" title={needAtlassianAccountTitle} message={needAtlassianAccountMessage}></InfoPanel>
    </div>
  );
};
